import type { Question } from "../../data/assessmentData";

export const assessmentQuestionsEn: Question[] = [
  // Strategy & Steering (5 questions)
  {
    id: 1,
    pillar: "Strategy & Steering",
    question: "Is your positioning clear?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Ask three people internally and you'll get four different answers.",
      },
      {
        id: "b",
        level: 2,
        text: "There is a **general idea**, but everyone rephrases it their own way. The website says one thing, sales says another.",
      },
      {
        id: "c",
        level: 3,
        text: "The positioning is **defined and documented**. The team knows it, but it's not always reflected in every communication.",
      },
      {
        id: "d",
        level: 4,
        text: "**Clear, shared** positioning internally and reflected everywhere. Clients confirm it spontaneously.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 2,
    pillar: "Strategy & Steering",
    question: "Is your acquisition strategy structured?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our acquisition strategy is word of mouth and hoping the phone rings.",
      },
      {
        id: "b",
        level: 2,
        text: "We run **occasional actions** (trade shows, LinkedIn campaigns) but without an overall plan or clear objectives.",
      },
      {
        id: "c",
        level: 3,
        text: "A plan exists with **identified channels**, a budget and recurring campaigns. Results are tracked, but **not linked to the pipeline**.",
      },
      {
        id: "d",
        level: 4,
        text: "**Documented strategy** by channel, with quantified objectives, budget, active campaigns and monthly tracking **linked to the pipeline**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 3,
    pillar: "Strategy & Steering",
    question: "How does collaboration between marketing and sales work?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Sales says the leads are poor. Marketing says sales never follow up. It's complicated.",
      },
      {
        id: "b",
        level: 2,
        text: "We exchange information **from time to time**, but marketing doesn't always know what sales does with the leads, and vice versa.",
      },
      {
        id: "c",
        level: 3,
        text: "**Regular meetings** and a shared definition of a good lead. But tools and data are not yet shared.",
      },
      {
        id: "d",
        level: 4,
        text: "Shared CRM, regular meetings, shared reporting. Both teams **work from the same KPIs**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 4,
    pillar: "Strategy & Steering",
    question:
      "How do you decide what's a priority in marketing when everything seems urgent?",
    options: [
      {
        id: "a",
        level: 1,
        text: "The priority is whatever the CEO saw at a competitor this morning, or what the loudest salesperson is demanding.",
      },
      {
        id: "b",
        level: 2,
        text: "We have a sense of priorities, but they **change often** based on urgencies and internal requests.",
      },
      {
        id: "c",
        level: 3,
        text: "Priorities are **defined at the start of each quarter**. Adjustments are made, but the direction stays clear.",
      },
      {
        id: "d",
        level: 4,
        text: "Prioritisation on objective criteria (impact, effort, strategic alignment). **Quarterly plan reviewed** with leadership.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 5,
    pillar: "Strategy & Steering",
    question:
      "Do you have dashboards that show the performance of your marketing at a glance?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our dashboard is a general gut feeling and a quick look at Google Analytics once a quarter.",
      },
      {
        id: "b",
        level: 2,
        text: "We look at stats from each tool separately (Analytics, LinkedIn, email) but **without a consolidated view**.",
      },
      {
        id: "c",
        level: 3,
        text: "A dashboard brings together the **main KPIs**. It's checked regularly but updated manually.",
      },
      {
        id: "d",
        level: 4,
        text: "**Automated dashboard** pulling all sources, reviewed weekly and used to drive decisions.",
      },
    ],
    correctAnswer: "d",
  },

  // Brand & Experience (6 questions)
  {
    id: 6,
    pillar: "Brand & Experience",
    question:
      "Is your image consistent and recognisable across your website, print materials, and social media?",
    options: [
      {
        id: "a",
        level: 1,
        text: "We have a logo. Actually, three. Plus the one someone redid in PowerPoint for a trade show.",
      },
      {
        id: "b",
        level: 2,
        text: "We have a logo and defined colours, but **everyone tweaks** their presentations and visuals their own way.",
      },
      {
        id: "c",
        level: 3,
        text: "A **brand guidelines document** exists and most materials follow it. A few gaps remain (old documents, outdated templates).",
      },
      {
        id: "d",
        level: 4,
        text: "Complete guidelines **applied everywhere**: website, social media, presentations, email signatures, print. Every asset reinforces the brand.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 7,
    pillar: "Brand & Experience",
    question:
      "Do your materials (brochures, presentations, product sheets) help your salespeople sell?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our salespeople redo their own PowerPoint for every meeting. Nobody uses the same documents.",
      },
      {
        id: "b",
        level: 2,
        text: "Materials exist, but they are **outdated** or no longer match what we actually sell.",
      },
      {
        id: "c",
        level: 3,
        text: "Materials are **up to date and used**. They could be more impactful or better tailored to specific audiences.",
      },
      {
        id: "d",
        level: 4,
        text: "Materials are tailored by target audience and sales cycle stage, and they **convert**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 8,
    pillar: "Brand & Experience",
    question:
      "Does a visitor to your website understand what you do within seconds?",
    options: [
      {
        id: "a",
        level: 1,
        text: 'Our homepage says "Innovative solutions for a world in motion". Nobody knows what that means.',
      },
      {
        id: "b",
        level: 2,
        text: "The site explains our services, but **you have to dig** to understand what we concretely do.",
      },
      {
        id: "c",
        level: 3,
        text: "The **message is clear**, but the journey could better guide visitors towards a concrete action.",
      },
      {
        id: "d",
        level: 4,
        text: "Clear message from the homepage, **journey designed** by target, calls to action that convert.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 9,
    pillar: "Brand & Experience",
    question: "Do you use a CRM to track your prospects and clients?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our CRM is an Excel file nobody updates, with 4 versions floating around on the server.",
      },
      {
        id: "b",
        level: 2,
        text: "We have a **CRM**, but only part of the team uses it. Data is **not up to date**.",
      },
      {
        id: "c",
        level: 3,
        text: "The CRM is **used by part of the team**. Marketing and sales don't really collaborate in it.",
      },
      {
        id: "d",
        level: 4,
        text: "**Centralised CRM, used** by sales AND marketing. Segmented contacts, complete history, reliable data.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 10,
    pillar: "Brand & Experience",
    question:
      "Do your marketing tools (website, email, CRM, analytics) talk to each other?",
    options: [
      {
        id: "a",
        level: 1,
        text: "To know what's happening, you need six passwords, four tabs, and a talent for copy-paste.",
      },
      {
        id: "b",
        level: 2,
        text: "Some connections exist (website form → CRM) but **a lot is done manually**.",
      },
      {
        id: "c",
        level: 3,
        text: "The **main tools** are **connected**, but some data still needs to be exported/imported by hand.",
      },
      {
        id: "d",
        level: 4,
        text: "Integrated ecosystem: website, CRM, email and analytics communicate **automatically**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 11,
    pillar: "Brand & Experience",
    question: "Is your website technically performant?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our site takes so long to load you can make a coffee while you wait. On mobile, it's worse.",
      },
      {
        id: "b",
        level: 2,
        text: "The site works broadly, but **we don't measure** its load speed or SEO scores.",
      },
      {
        id: "c",
        level: 3,
        text: "The site is **technically sound** and mobile-friendly. Speed and technical SEO could be improved.",
      },
      {
        id: "d",
        level: 4,
        text: "Fast, mobile-first site, optimised technical SEO, with **regular performance monitoring**.",
      },
    ],
    correctAnswer: "d",
  },

  // Content & Visibility (6 questions)
  {
    id: 12,
    pillar: "Content & Visibility",
    question:
      "Do you regularly produce content (articles, LinkedIn posts, videos, case studies, ...) to attract prospects?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our last blog post was in 2019. It was about COVID. Since then, radio silence.",
      },
      {
        id: "b",
        level: 2,
        text: "We publish **from time to time**, when someone has the time or inspiration. No rhythm or editorial plan.",
      },
      {
        id: "c",
        level: 3,
        text: "An editorial calendar exists and **we publish regularly**. The content could be more targeted.",
      },
      {
        id: "d",
        level: 4,
        text: "**Industrialised production**: editorial calendar, varied formats, consistent publishing.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 13,
    pillar: "Content & Visibility",
    question:
      "Is your content aimed at a specific audience, or do you speak to everyone?",
    options: [
      { id: "a", level: 1, text: "We speak to everyone." },
      {
        id: "b",
        level: 2,
        text: "Content is **not tailored to specific profiles**.",
      },
      {
        id: "c",
        level: 3,
        text: "Most content is **designed for our main target audiences**.",
      },
      {
        id: "d",
        level: 4,
        text: "Content adapted to **each profile** and **each stage** of the buying journey.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 14,
    pillar: "Content & Visibility",
    question: "Does your content stand out from your competitors'?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Honestly, if you put a competitor's logo on our content, nobody would notice the difference.",
      },
      {
        id: "b",
        level: 2,
        text: "We have our own content, but the tone and topics **look like what everyone else does** in the sector.",
      },
      {
        id: "c",
        level: 3,
        text: "Our content has a **defined identity** (tone, topics, angle). Our challenge is maintaining consistency across all channels.",
      },
      {
        id: "d",
        level: 4,
        text: "Distinctive voice, varied formats, recognisable editorial line **across all communication channels**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 15,
    pillar: "Content & Visibility",
    question: "How do you distribute your content?",
    options: [
      {
        id: "a",
        level: 1,
        text: "We publish an article on the site and hope someone finds it.",
      },
      {
        id: "b",
        level: 2,
        text: "We share on **a few channels** (LinkedIn, newsletter) but without a structured plan or repurposing.",
      },
      {
        id: "c",
        level: 3,
        text: "A **distribution plan** exists: each piece of content is shared across multiple channels on a schedule. We're starting to repurpose.",
      },
      {
        id: "d",
        level: 4,
        text: "**Orchestrated distribution**: each channel has a defined role (awareness, acquisition, nurturing).",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 16,
    pillar: "Content & Visibility",
    question:
      "Are you visible and active on the channels where your clients are?",
    options: [
      {
        id: "a",
        level: 1,
        text: "We created a LinkedIn page in 2020. It has our logo and a description. Since then, nothing.",
      },
      {
        id: "b",
        level: 2,
        text: "We're findable **on a few channels** but without any structured effort behind it.",
      },
      {
        id: "c",
        level: 3,
        text: "**Visible presence on our main channels** thanks to regular content.",
      },
      {
        id: "d",
        level: 4,
        text: "**Coordinated multi-channel strategy** that attracts qualified leads.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 17,
    pillar: "Content & Visibility",
    question: "Do clients find you when they search for your services?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Search our main service on Google and you find our competitors. To find us, you need to type our exact name.",
      },
      {
        id: "b",
        level: 2,
        text: "We've done some **basic optimisations** for Google (titles, keywords) but without a structured SEO strategy.",
      },
      {
        id: "c",
        level: 3,
        text: "**SEO is taken into account**: keywords identified, content optimised, rankings tracked. A systematic approach is still missing.",
      },
      {
        id: "d",
        level: 4,
        text: "**Full SEO strategy** to be found on Google, but also in AI tools like ChatGPT.",
      },
    ],
    correctAnswer: "d",
  },

  // AI & Tools (6 questions)
  {
    id: 18,
    pillar: "AI & Tools",
    question: "How do you manage your marketing projects?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Our project management tool is a full inbox and Post-it notes on the screen. Sometimes a shared Excel file.",
      },
      {
        id: "b",
        level: 2,
        text: "We have a tool (Trello, Excel...) but it's **not used systematically** by the whole team.",
      },
      {
        id: "c",
        level: 3,
        text: "A project management **tool is in place and used**. Tasks tracked, deadlines **broadly met**.",
      },
      {
        id: "d",
        level: 4,
        text: "**Structured management** (Kanban or sprints), team rituals, clear priorities, **weekly tracking**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 19,
    pillar: "AI & Tools",
    question: "Do you measure the ROI of your marketing?",
    options: [
      {
        id: "a",
        level: 1,
        text: "No idea. Marketing is an act of faith. We invest and hope it works.",
      },
      {
        id: "b",
        level: 2,
        text: "We have a **general idea of costs**, but linking an investment to a concrete business result remains difficult.",
      },
      {
        id: "c",
        level: 3,
        text: "**ROI is measured for main actions** (campaigns, trade shows). A consolidated view linking spend and pipeline is still missing.",
      },
      {
        id: "d",
        level: 4,
        text: "**ROI** measured by channel, linked to business results (leads, meetings, contracts) and **consolidated in a dashboard**.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 20,
    pillar: "AI & Tools",
    question:
      "Do you use AI in your marketing activities: writing, analysis, visuals, automation?",
    options: [
      {
        id: "a",
        level: 1,
        text: "AI? We asked ChatGPT to write an email once. Does that count?",
      },
      {
        id: "b",
        level: 2,
        text: "A few people use AI tools **occasionally** (ChatGPT, Copilot), but without a framework or knowledge sharing.",
      },
      {
        id: "c",
        level: 3,
        text: "AI tools are **integrated in some marketing processes** (writing, analysis, visuals). Usage is guided but not systematic.",
      },
      {
        id: "d",
        level: 4,
        text: "**AI deployed strategically**: integrated tools, documented prompts, measured gains.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 21,
    pillar: "AI & Tools",
    question:
      "Is your team trained on AI, or does everyone figure it out on their own?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Train the team? We're waiting for the intern to show us how it works.",
      },
      {
        id: "b",
        level: 2,
        text: "**A few people experiment**, but no structured sharing of learnings or best practices.",
      },
      {
        id: "c",
        level: 3,
        text: "Training or workshops have taken place. **The team knows the basics**, but adoption remains uneven.",
      },
      {
        id: "d",
        level: 4,
        text: "**Ongoing training**, **shared** best practices, active monitoring. AI is part of the team culture.",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 22,
    pillar: "AI & Tools",
    question: "Is AI actually saving you time or money in a measurable way?",
    options: [
      {
        id: "a",
        level: 1,
        text: "We don't know if AI saves us time. We spend so much time correcting its outputs that the question is open.",
      },
      {
        id: "b",
        level: 2,
        text: "**Some occasional gains** (faster writing, content ideas), but nothing formally measured.",
      },
      {
        id: "c",
        level: 3,
        text: "Gains are identified and acknowledged (productivity, quality). **We're starting to quantify them.**",
      },
      {
        id: "d",
        level: 4,
        text: "Impact = time saved per process, reduced cost per deliverable, improved quality. **The numbers justify the investment.**",
      },
    ],
    correctAnswer: "d",
  },
  {
    id: 23,
    pillar: "AI & Tools",
    question: "Do you have clear rules for AI usage?",
    options: [
      {
        id: "a",
        level: 1,
        text: "Rules? We don't even have clear rules for using the coffee machine.",
      },
      {
        id: "b",
        level: 2,
        text: "**It's implicit**: everyone knows not to share confidential data, but nothing is written down.",
      },
      {
        id: "c",
        level: 3,
        text: "**Guidelines exist** (authorised data, human validation required). Communicated but not always applied.",
      },
      {
        id: "d",
        level: 4,
        text: "**Documented and enforced AI policy**: authorised data, validation process, GDPR compliance, regular review.",
      },
    ],
    correctAnswer: "d",
  },
];
